import React, { useState, useEffect, useCallback } from 'react';
import { notification, Button, Modal, Space, Typography } from 'antd';
import { 
  ReloadOutlined, 
  ExclamationCircleOutlined,
  CheckCircleOutlined 
} from '@ant-design/icons';

const { Text, Title } = Typography;

/**
 * Componente para mostrar notificaciones de actualización de versión
 */
const UpdateNotification = ({ 
  isUpdateAvailable, 
  currentVersion, 
  serverVersion, 
  onUpdate, 
  onDismiss 
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [notificationShown, setNotificationShown] = useState(false);

  // Función para manejar actualización
  const handleUpdate = useCallback(() => {
    Modal.confirm({
      title: '¿Confirmar actualización?',
      icon: <ReloadOutlined />,
      content: (
        <div>
          <p>
            La aplicación se actualizará de la versión <strong>{currentVersion}</strong> a 
            la versión <strong>{serverVersion}</strong>.
          </p>
          <p>
            <Text type="warning">
              ⚠️ Esto recargará la página y podrías perder datos no guardados.
            </Text>
          </p>
        </div>
      ),
      okText: 'Actualizar',
      cancelText: 'Cancelar',
      onOk: () => {
        if (onUpdate) {
          onUpdate();
        }
      },
    });
  }, [currentVersion, serverVersion, onUpdate]);

  // Función para mostrar notificación (definida después del handleUpdate)
  const showUpdateNotification = useCallback(() => {
    const key = 'version-update';
    
    // En Ant Design v5, usamos destroy para cerrar notificaciones específicas
    const closeNotification = () => {
      notification.destroy(key);
    };
    
    notification.open({
      key,
      message: '🆕 Nueva versión disponible',
      description: (
        <div>
          <Text>
            Hay una nueva versión de MOGOTAX disponible.
          </Text>
          <br />
          <Text type="secondary">
            Versión actual: {currentVersion} → Nueva: {serverVersion}
          </Text>
        </div>
      ),
      duration: 0, // No auto-cerrar
      icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
      actions: [
        <Button 
          key="details"
          size="small" 
          onClick={() => {
            closeNotification();
            setIsModalVisible(true);
          }}
        >
          Ver Detalles
        </Button>,
        <Button 
          key="update"
          type="primary" 
          size="small" 
          icon={<ReloadOutlined />}
          onClick={() => {
            closeNotification();
            handleUpdate();
          }}
        >
          Actualizar Ahora
        </Button>
      ],
      onClose: () => {
        if (onDismiss) onDismiss();
      },
      placement: 'topRight',
      style: {
        width: 400,
      },
    });
  }, [currentVersion, serverVersion, onDismiss, handleUpdate]);

  // Mostrar notificación cuando hay actualización disponible
  useEffect(() => {
    if (isUpdateAvailable && !notificationShown) {
      showUpdateNotification();
      setNotificationShown(true);
    }
  }, [isUpdateAvailable, notificationShown, showUpdateNotification]);

  // Resetear cuando se resuelve la actualización
  useEffect(() => {
    if (!isUpdateAvailable) {
      setNotificationShown(false);
      setIsModalVisible(false);
    }
  }, [isUpdateAvailable]);

  const handleModalClose = () => {
    setIsModalVisible(false);
    if (onDismiss) onDismiss();
  };

  return (
    <>
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#faad14' }} />
            Nueva Versión Disponible
          </Space>
        }
        open={isModalVisible}
        onCancel={handleModalClose}
        footer={[
          <Button key="later" onClick={handleModalClose}>
            Recordar Después
          </Button>,
          <Button 
            key="update" 
            type="primary" 
            icon={<ReloadOutlined />}
            onClick={handleUpdate}
          >
            Actualizar Ahora
          </Button>
        ]}
        width={500}
      >
        <div style={{ padding: '16px 0' }}>
          <Title level={5}>Información de la Actualización</Title>
          
          <div style={{ marginBottom: 16 }}>
            <Text strong>Versión Actual: </Text>
            <Text code>{currentVersion}</Text>
          </div>
          
          <div style={{ marginBottom: 16 }}>
            <Text strong>Nueva Versión: </Text>
            <Text code style={{ color: '#52c41a' }}>{serverVersion}</Text>
          </div>
          
          <div style={{ marginBottom: 16 }}>
            <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
            <Text>
              Se recomienda actualizar para obtener las últimas mejoras y correcciones.
            </Text>
          </div>
          
          <div style={{ 
            padding: 12, 
            backgroundColor: '#fff7e6', 
            borderRadius: 6,
            border: '1px solid #ffd591'
          }}>
            <Text type="warning">
              <strong>Nota:</strong> La actualización recargará completamente la aplicación.
              Asegúrate de guardar tu trabajo antes de continuar.
            </Text>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default UpdateNotification;